import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';
// Phase 1 note: React is loaded via dynamic import inside mfe-env-config-runtime-definitions
// to avoid duplicate static import conflicts with other plugins (e.g. tutor-indigo).

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0D0D0E;
            color: #ccc;
          }
          a { color: #ccc; }
          a:hover { color: #d3d3d3; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

// Pins the Paragon theme variant to "light" on every page.
//
// tutor-indigo ships a dark-mode toggle whose state lives in two stores that
// must agree with each other:
//   - cookie "selected-paragon-theme-variant" — read by indigo's dark-theme.js
//     on legacy LMS pages, and by its ToggleThemeButton inside MFEs
//   - localStorage under the same name — read by indigo's AddDarkTheme, which
//     is what actually puts data-paragon-theme-variant="dark" on <html>
// ToggleThemeButton reloads the page whenever the two disagree, so localStorage
// is aligned only after the cookie write is confirmed; a rejected cookie (an
// MFE or Studio host outside the LMS domain) would otherwise reload forever.
//
// The cookie is written with the LMS hostname as domain — the same scope
// indigo uses — so it overwrites indigo's cookie instead of creating a second
// one with the same name at a narrower scope.
//
// HUIT builds only a light Paragon variant, so any leftover "dark" state
// renders unreadable pages. Deleting this file restores indigo's toggle.
//
// Shared verbatim by two surfaces: the MFE env.config.jsx (module scope, so it
// runs before React mounts and before the effects above) and the <head> script
// that HuitCssInjectMiddleware injects into legacy LMS/CMS responses.
(function forceLightParagonTheme() {
  var KEY = 'selected-paragon-theme-variant';
  var LIGHT = 'light';
  var COOKIE_MAX_AGE_DAYS = 90;

  function readCookie() {
    var match = document.cookie.split('; ').find(function (row) {
      return row.indexOf(KEY + '=') === 0;
    });
    return match ? match.split('=')[1] : undefined;
  }

  var expires = new Date(Date.now() + COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
  document.cookie = KEY + '=' + LIGHT
    + '; domain=lms.huit.edu.vn'
    + '; path=/'
    + '; expires=' + expires.toUTCString()
    + '; SameSite=Lax';

  if (readCookie() === LIGHT) {
    try {
      window.localStorage.setItem(KEY, LIGHT);
    } catch (e) {
      // localStorage unavailable (private mode / storage disabled)
    }
  }

  document.documentElement.setAttribute('data-paragon-theme-variant', LIGHT);
})();
// Shared CSS for all MFE header variants:
//   - Standard header  → injected via logo_slot
//   - Learning header  → same slot, scoped to .learning-header
//   - Studio header    → injected via studio_header_search_button_slot.v1
// Injected as a <style> tag via whichever slot the MFE actually renders.
const HUIT_HEADER_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');

  /* Global Font Overrides */
  body, h1, h2, h3, h4, h5, h6, p, label, button, input, select, textarea, span, a {
    font-family: Roboto, Arial, Helvetica, sans-serif;
  }

  .site-header-desktop,
  header.site-header {
    background-color: #034EA2 !important;
    padding: 6px 0 !important;
  }
  .site-header-desktop .nav-link,
  .site-header-desktop .nav-container > a,
  .site-header-desktop .dropdown-menu-button {
    color: #ffffff !important;
  }
  .learning-header {
    background-color: #034EA2 !important;
    padding: 10px 0 !important;
    border-bottom: 1px solid #ffffff !important;
  }
  .learning-header .course-title-lockup {
    color: #ffffff !important;
  }
  .site-header-mobile {
    background-color: #034EA2 !important;
  }
  .site-header-mobile a,
  .site-header-mobile .hamburger-btn,
  .site-header-mobile .icon-button {
    color: #ffffff !important;
  }
  #root header.site-header-desktop {
    box-shadow: none !important;
  }

  /* ── Studio Header (frontend-app-authoring) HUIT branding ── */
  .studio-header {
    background-color: #034EA2 !important;
  }
  .studio-header .logo {
    height: 40px !important;
    width: auto !important;
  }
  /* Dropdown/action buttons — outline-primary on dark bg */
  .studio-header .btn.btn-outline-primary {
    color: #ffffff !important;
    background-color: transparent !important;
    border: none !important;
  }
  .studio-header .btn.btn-outline-primary:hover,
  .studio-header .btn.btn-outline-primary:focus,
  .studio-header .btn.btn-outline-primary.show {
    background-color: rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;
    border: none !important;
    &:before {
      display: none !important;
    }
  }
  /* Mobile hamburger/close button */
  .studio-header .btn.btn-tertiary {
    color: #ffffff !important;
  }
  .studio-header .btn.btn-tertiary:hover,
  .studio-header .btn.btn-tertiary:focus {
    background-color: rgba(255, 255, 255, 0.15) !important;
  }
  /* Course org/number/title text (overrides Paragon text-gray-800) */
  .studio-header .course-title-lockup span {
    color: #ffffff !important;
  }
  /* Divider between logo and course lockup */
  .studio-header .course-title-lockup {
    border-right-color: rgba(255, 255, 255, 0.3) !important;
  }

  @media only screen and (min-width: 842px) {
    .studio-header {
      height: auto !important;
      min-height: 7rem !important;
    }

    .studio-header header {
      align-items: center !important;
      flex-wrap: wrap !important;
      min-height: 7rem !important;
      padding-top: 0.35rem !important;
      padding-bottom: 0 !important;
    }

    .studio-header header > .w-25 {
      flex: 0 1 34% !important;
      max-width: 34% !important;
      width: auto !important;
    }

    .studio-header [data-testid="desktop-menu"] {
      order: 10 !important;
      flex: 0 0 100% !important;
      width: 100% !important;
      display: flex !important;
      justify-content: start !important;
      gap: 0.5rem !important;
      margin: 0.35rem 0 0 !important;
      padding: 0.45rem 0 0.55rem !important;
      border-top: 1px solid rgba(255, 255, 255, 0.24) !important;
    }

    .studio-header [data-testid="desktop-menu"] .btn.btn-outline-primary {
      min-width: 8rem !important;
    }

    .studio-header [data-testid="desktop-menu"] .dropdown-menu {
      z-index: 1100 !important;
    }
  }

  /* Hide the dark/light mode toggle switch from all headers */
  .site-header-desktop .switch,
  header.site-header .switch,
  .learning-header .switch,
  .site-header-mobile .switch,
  .studio-header .switch {
    display: none !important;
  }
`;
// HUIT logo for logo_slot — widget logo duy nhất của header MFE.
const HuitLogoLegacy = () => {
  const cfg = getConfig();
  return React.createElement('a', {
    href: cfg.LMS_BASE_URL || '/',
    title: 'HUIT',
    className: 'logo',
    style: { display: 'inline-flex', alignItems: 'center' },
  },
    React.createElement('img', {
      className: 'logo-image',
      src: 'https://cdn.office.huit.edu.vn/openedx-assets/assets/images/logo.png',
      alt: 'HUIT',
      style: { height: '40px', width: 'auto' },
    }),
  );
};

// Desktop logged-out CTA for standard header slot.
// const HuitDesktopLoggedOut = () => {
//   const { formatMessage } = useIntl();
//   const cfg = getConfig();
//   return React.createElement(
//     React.Fragment, null,
//     React.createElement('a', {
//       className: 'btn mr-2 btn-link',
//       style: { color: '#ffffff' },
//       href: cfg.LOGIN_URL,
//     }, formatMessage({ id: 'general.signIn.sentenceCase', defaultMessage: 'Đăng nhập' })),
//     React.createElement('a', {
//       className: 'btn mr-2 btn-outline-primary',
//       style: { color: '#ffffff', borderColor: 'rgba(255,255,255,0.6)' },
//       href: cfg.REGISTER_URL || (cfg.LMS_BASE_URL + '/register'),
//     }, formatMessage({ id: 'general.register.sentenceCase', defaultMessage: 'Đăng ký' })),
//   );
// };

// Mobile logged-out CTA for standard header slot.
// const HuitMobileLoggedOut = () => {
//   const { formatMessage } = useIntl();
//   const cfg = getConfig();
//   return React.createElement(
//     React.Fragment, null,
//     React.createElement('li', { className: 'nav-item px-3 my-2' },
//       React.createElement('a', {
//         className: 'btn btn-block btn-outline-primary',
//         style: { color: '#ffffff', borderColor: 'rgba(255,255,255,0.6)' },
//         href: cfg.LOGIN_URL,
//       }, formatMessage({ id: 'general.signIn.sentenceCase', defaultMessage: 'Đăng nhập' })),
//     ),
//     React.createElement('li', { className: 'nav-item px-3 my-2' },
//       React.createElement('a', {
//         className: 'btn btn-block btn-primary',
//         href: cfg.REGISTER_URL || (cfg.LMS_BASE_URL + '/register'),
//       }, formatMessage({ id: 'general.register.sentenceCase', defaultMessage: 'Đăng ký' })),
//     ),
//   );
// };
// HUIT MFE Footer CSS — injected via <style> in HuitFooter component.
// Mirrors the .huit-footer-* rules in huit-override.css so MFE footer
// looks identical to the Django-rendered LMS footer.
// Jinja2 vars (#034EA2) are evaluated at `tutor config save`.

const HUIT_FOOTER_MFE_CSS = `
  .huit-footer {
    background: #034EA2;
    color: #fff;
    padding: 40px max(24px, calc((100vw - 1600px) / 2));
  }
  .huit-footer-inner {
    align-items: flex-start;
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 40px;
  }
  .huit-footer-col--left {
    border-right: 2px solid #fff;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-right: 40px;
  }
  .huit-footer-col--right {
    display: grid;
    gap: 20px 32px;
    grid-template-columns: 3fr 1fr 1fr;
    height: 100%;
  }
  .huit-footer-logo-link {
    display: block;
    line-height: 0;
    text-decoration: none !important;
  }
  .huit-footer-logo {
    height: 70px;
    max-height: 68px;
    width: auto;
  }
  .huit-footer-social {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: space-between;
    margin-top: 20px;
    width: 100%;
  }
  .huit-social-btn {
    align-items: center;
    border-radius: 12px;
    color: #fff;
    display: flex;
    height: 44px;
    justify-content: center;
    text-decoration: none !important;
    transition: opacity .2s ease, transform .15s ease;
    width: 44px;
  }
  .huit-social-btn:hover, .huit-social-btn:focus {
    opacity: .85;
    text-decoration: none !important;
    transform: translateY(-2px);
  }
  .huit-social-btn svg { height: 24px; width: 24px; }
  .huit-social-youtube  { background: #FF0000; }
  .huit-social-instagram{ background: #F77737; }
  .huit-social-zalo     { background: transparent; overflow: hidden; padding: 0; }
  .huit-social-zalo svg { height: 44px; width: 44px; }
  .huit-social-facebook { background: #1877F2; }
  .huit-social-tiktok   { background: #010101; }
  .huit-footer-school-name {
    color: #fff;
    font-size: 20px;
    font-weight: 800;
    grid-column: span 3;
    letter-spacing: .01em;
    line-height: 1.4;
    margin: 0;
    text-transform: uppercase;
  }
  .huit-footer-campus {
    align-items: center;
    color: rgba(255,255,255,.9);
    display: flex;
    font-size: 16px;
    gap: 6px;
    line-height: 1.5;
    margin: 0 0 8px;
    white-space: nowrap;
  }
  .huit-footer-campus-pin { flex-shrink: 0; margin-top: 2px; opacity: .75; }
  .huit-footer-campus strong { font-weight: 700; white-space: nowrap; }
  .huit-footer-contact-block { display: flex; flex-direction: column; margin-bottom: 16px; }
  .huit-footer-contact-label {
    align-items: center;
    color: #fff !important;
    display: flex;
    font-size: 16px;
    font-weight: 800;
    gap: 5px;
    letter-spacing: .08em;
    margin: 0 0 12px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .huit-footer-contact-icon { display: inline-flex; opacity: .75; }
  .huit-footer-phone, .huit-footer-email {
    color: #fff !important;
    font-size: 16px;
    font-weight: 600;
    text-decoration: none !important;
    white-space: nowrap;
  }
  .huit-footer-phone:hover, .huit-footer-email:hover { text-decoration: underline !important; }
  .huit-footer-sitemap-list { display: flex; flex-direction: column; gap: 8px; list-style: none; margin: 0; padding: 0; }
  .huit-footer-sitemap-link {
    color: #fff !important;
    font-size: 16px;
    text-decoration: none !important;
    white-space: nowrap;
  }
  .huit-footer-sitemap-link:hover { color: #fff !important; text-decoration: underline !important; }
  .huit-footer-contacts, .huit-footer-sitemap-container { padding-top: 10px; }
  @media (max-width: 1200px) {
    .huit-footer-inner { gap: 28px; grid-template-columns: 1fr; }
    .huit-footer-col--left {
      align-items: center; border-bottom: 2px solid #fff; border-right: none;
      flex-direction: row; gap: 24px; justify-content: space-around;
      padding-bottom: 24px; padding-right: 0;
    }
    .huit-footer-logo { height: 50px; }
    .huit-footer-social { justify-content: space-around; margin-top: 0; }
    .huit-footer-col--right {
      grid-template-columns: max-content max-content;
      justify-content: center;
      padding-top: 24px;
    }
    .huit-footer-school-name { grid-column: span 2; margin-bottom: 0; }
    .huit-footer-campuses { grid-column: span 2; }
    .huit-footer-contacts, .huit-footer-sitemap-container { padding-top: 0; }
  }
  @media (max-width: 575px) {
    .huit-footer-school-name { font-size: 16px !important; }
    .huit-footer-campus { font-size: 12px !important; }
    .huit-footer-campus strong { font-size: 14px !important; }
    .huit-footer-contact-label { font-size: 14px !important; gap: 4px !important; }
    .huit-footer-phone, .huit-footer-email { font-size: 12px !important; }
    .huit-footer-sitemap-link { font-size: 12px !important; }
  }
`;
// HUIT MFE Footer component — mirrors footer.html (Django/LMS) structure.
// Jinja2 data constants are evaluated at `tutor config save`.
// React, HUIT_FOOTER_MFE_CSS are available from buildtime-definitions scope.
// SVG paths from footer.html are embedded inline.

// ── Tutor config data (Jinja2-rendered at buildtime) ──
const HUIT_FOOTER_LMS_URL = 'https://lms.huit.edu.vn';
const HUIT_FOOTER_LOGO_URL = "https://cdn.office.huit.edu.vn/openedx-assets/assets/images/logo.png";
const HUIT_FOOTER_SOCIAL_LINKS = [{"icon": "youtube", "name": "YouTube", "url": "https://www.youtube.com/DHCongthuong"}, {"icon": "instagram", "name": "Instagram", "url": "https://www.instagram.com/dh_congthuong/"}, {"icon": "zalo", "name": "Zalo", "url": "https://zalo.me/4418938306145458374"}, {"icon": "facebook", "name": "Facebook", "url": "https://www.facebook.com/DhCongThuongHCM"}, {"icon": "tiktok", "name": "TikTok", "url": "https://vt.tiktok.com/ZSJNKjowo"}];
const HUIT_FOOTER_CAMPUSES = [{"address": "140 L\u00ea Tr\u1ecdng T\u1ea5n, P. T\u00e2y Th\u1ea1nh, Th\u00e0nh ph\u1ed1 H\u1ed3 Ch\u00ed Minh", "label": "C\u01a1 s\u1edf ch\u00ednh"}, {"address": "93 T\u00e2n K\u1ef3 T\u00e2n Qu\u00fd, P. T\u00e2n S\u01a1n Nh\u00ec, TP. H\u1ed3 Ch\u00ed Minh", "label": "Trung t\u00e2m Th\u00ed nghi\u1ec7m Th\u1ef1c h\u00e0nh"}, {"address": "73/1 Nguy\u1ec5n \u0110\u1ed7 Cung, P. T\u00e2y Th\u1ea1nh, TP. H\u1ed3 Ch\u00ed Minh", "label": "Khoa Gi\u00e1o d\u1ee5c th\u1ec3 ch\u1ea5t v\u00e0 Qu\u1ed1c ph\u00f2ng An ninh"}, {"address": "247 T\u00e2n K\u1ef3 T\u00e2n Qu\u00fd, P. T\u00e2n S\u01a1n Nh\u00ec, TP. H\u1ed3 Ch\u00ed Minh", "label": "Trung t\u00e2m Gi\u00e1o d\u1ee5c Ph\u1ed5 th\u00f4ng"}, {"address": "31 Ch\u1ebf Lan Vi\u00ean, P. T\u00e2y Th\u1ea1nh, TP. H\u1ed3 Ch\u00ed Minh", "label": "C\u01a1 s\u1edf th\u1ef1c h\u00e0nh C\u01a1 Kh\u00ed, \u0110i\u1ec7n - \u0110i\u1ec7n T\u1eed, May- Th\u1eddi trang, Dinh D\u01b0\u1ee1ng v\u00e0 \u1ea8m th\u1ef1c"}, {"address": "263 L\u00ea Tr\u1ecdng T\u1ea5n, P. S\u01a1n K\u1ef3, TP. H\u1ed3 Ch\u00ed Minh", "label": "C\u01a1 s\u1edf 263 L\u00ea Tr\u1ecdng T\u1ea5n"}, {"address": "102-104-106 Nguy\u1ec5n Qu\u00fd Anh, P. T\u00e2n S\u01a1n Nh\u00ec, TP. H\u1ed3 Ch\u00ed Minh", "label": "Trung t\u00e2m K\u00fd t\u00fac x\u00e1"}];
const HUIT_FOOTER_HOTLINE = "0283.8163.318";
const HUIT_FOOTER_EMAIL = "info@huit.edu.vn";
const HUIT_FOOTER_SITEMAP_LINKS = [{"label": "Gi\u1edbi thi\u1ec7u", "url": "/about"}, {"label": "Kh\u00f3a h\u1ecdc", "url": "/courses"}, {"label": "Tra c\u1ee9u v\u0103n b\u1eb1ng", "url": "https://sinhvien.huit.edu.vn/Tra-Cuu-Van-Bang.html"}, {"label": "Tuy\u1ec3n sinh", "url": "https://ts.huit.edu.vn/"}, {"label": "Li\u00ean h\u1ec7", "url": "/help"}];

const huitFooterHref = (url) => (typeof url === 'string' && url.startsWith('/') ? `${HUIT_FOOTER_LMS_URL}${url}` : url);

// ── Social icon SVG factory (paths from footer.html) ──
const huitSocialIcon = (iconName) => {
  const e = React.createElement;
  const svgPaths = {
    youtube: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    facebook: 'M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z',
    tiktok: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
    instagram: 'M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M12.004 8.457A3.555 3.555 0 1 1 8.449 12.01 3.555 3.555 0 0 1 12.004 8.457',
  };

  // Zalo: multi-path SVG (different viewBox)
  if (iconName === 'zalo') {
    return e('svg', { viewBox: '0 0 50 50', fill: 'none', 'aria-hidden': 'true', key: 'ic' },
      e('path', { fillRule: 'evenodd', clipRule: 'evenodd', fill: '#0068FF', d: 'M22.782.166H27.199C33.265.166 36.81 1.057 39.957 2.744 43.104 4.431 45.588 6.896 47.256 10.043 48.943 13.19 49.834 16.735 49.834 22.801V27.199c0 6.066-.891 9.611-2.578 12.758-1.688 3.147-4.152 5.631-7.299 7.298-3.147 1.687-6.692 2.578-12.758 2.578H22.801c-6.066 0-9.611-.891-12.758-2.578C6.896 45.569 4.412 43.104 2.744 39.957 1.057 36.81.166 33.265.166 27.199V22.801C.166 16.735 1.057 13.19 2.744 10.043 4.431 6.896 6.896 4.412 10.043 2.744 13.171 1.057 16.735.166 22.782.166Z' }),
      e('path', { fillRule: 'evenodd', clipRule: 'evenodd', fill: 'white', d: 'M7.779 43.589c2.323.257 5.227-.405 7.289-1.406 8.954 4.949 22.951 4.713 31.424-.709.328-.493.635-1.005.92-1.537C49.106 36.778 50 33.22 50 27.132V22.718c0-6.09-.894-9.647-2.587-12.805C45.739 6.754 43.246 4.281 40.088 2.588 36.929.894 33.371 0 27.283 0H22.85c-5.186 0-8.552.653-11.38 1.899A12.58 12.58 0 0 0 11.015 2.32C2.717 10.32 2.087 27.659 9.123 37.078c.008.014.017.028.026.042 1.084 1.598.038 4.395-1.598 6.031-.267.248-.172.4.228.438Z' }),
      e('path', { fill: '#0068FF', d: 'M20.563 17H10.838v2.085h6.749l-6.654 8.247c-.209.303-.36.588-.36 1.232v.531h9.175c.455 0 .834-.379.834-.834v-1.119h-7.09l6.255-7.797a7.2 7.2 0 0 0 .757-.842l.038-.057c.36-.53.436-.985.436-1.535V17Z' }),
      e('path', { fill: '#0068FF', d: 'M32.942 29.095h1.384V17h-2.086v11.393c0 .38.304.702.702.702ZM25.814 19.692c-2.616 0-4.739 2.123-4.739 4.74 0 2.615 2.123 4.738 4.739 4.738 2.616 0 4.74-2.123 4.74-4.739.018-2.616-2.105-4.739-4.74-4.739Zm0 7.527c-1.536 0-2.787-1.252-2.787-2.787 0-1.536 1.251-2.787 2.787-2.787s2.787 1.251 2.787 2.787c0 1.535-1.232 2.787-2.787 2.787ZM40.487 19.616c-2.635 0-4.777 2.142-4.777 4.778 0 2.635 2.142 4.777 4.777 4.777s4.777-2.142 4.777-4.777c0-2.636-2.142-4.778-4.777-4.778Zm0 7.602c-1.554 0-2.806-1.251-2.806-2.806 0-1.554 1.252-2.806 2.806-2.806 1.555 0 2.806 1.252 2.806 2.806 0 1.555-1.232 2.806-2.806 2.806Z' }),
      e('path', { fill: '#0068FF', d: 'M29.456 29.094h1.119V19.957H28.622v8.322c0 .436.379.815.834.815Z' }),
    );
  }

  const path = svgPaths[iconName];
  if (!path) return null;
  return e('svg', { viewBox: '0 0 24 24', fill: '#fff', 'aria-hidden': 'true', key: 'ic' },
    e('path', { d: path }),
  );
};

// ── Footer component ──
const HuitFooter = () => {
  const e = React.createElement;
  const pinPath = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';
  const phonePath = 'M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z';
  const emailPath = 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z';

  const svgIcon = (path, w, h) => e('svg', { viewBox: '0 0 24 24', fill: '#fff', width: w, height: h, 'aria-hidden': 'true' }, e('path', { d: path }));

  return e(React.Fragment, null,
    e('style', { key: 'huit-footer-css' }, HUIT_FOOTER_MFE_CSS),
    e('footer', { role: 'contentinfo', className: 'huit-footer', 'aria-label': 'Site footer' },
      e('div', { className: 'huit-footer-inner' },

        // ── Left: logo + social ──
        e('div', { className: 'huit-footer-col huit-footer-col--left' },
          e('a', { href: HUIT_FOOTER_LMS_URL, className: 'huit-footer-logo-link', 'aria-label': 'Home' },
            e('img', { src: HUIT_FOOTER_LOGO_URL, alt: 'HUIT', className: 'huit-footer-logo' }),
          ),
          e('div', { className: 'huit-footer-social', 'aria-label': 'Social media' },
            ...HUIT_FOOTER_SOCIAL_LINKS.map((link) => e('a', {
              key: link.icon,
              href: link.url,
              className: `huit-social-btn huit-social-${link.icon}`,
              target: '_blank',
              rel: 'noopener noreferrer',
              'aria-label': link.name,
            }, huitSocialIcon(link.icon))),
          ),
        ),

        // ── Right: school name, campuses, contacts, sitemap ──
        e('div', { className: 'huit-footer-col huit-footer-col--right' },
          e('h2', { className: 'huit-footer-school-name' }, 'TRƯỜNG ĐẠI HỌC CÔNG THƯƠNG THÀNH PHỐ HỒ CHÍ MINH'),
          e('div', { className: 'huit-footer-campuses' },
            ...HUIT_FOOTER_CAMPUSES.map((campus, idx) => e('p', { key: campus.label, className: 'huit-footer-campus' },
              e('span', { className: 'huit-footer-campus-pin', 'aria-hidden': 'true', style: idx > 0 ? { opacity: 0 } : {} },
                svgIcon(pinPath, '24', '24'),
              ),
              e('strong', null, `${campus.label}: `),
              campus.address,
            )),
          ),
          e('div', { className: 'huit-footer-contacts' },
            HUIT_FOOTER_HOTLINE ? e('div', { className: 'huit-footer-contact-block', key: 'hotline' },
              e('p', { className: 'huit-footer-contact-label' },
                e('span', { className: 'huit-footer-contact-icon', 'aria-hidden': 'true' }, svgIcon(phonePath, '16', '16')),
                'HOTLINE',
              ),
              e('a', { href: `tel:${HUIT_FOOTER_HOTLINE}`, className: 'huit-footer-phone' }, HUIT_FOOTER_HOTLINE),
            ) : null,
            HUIT_FOOTER_EMAIL ? e('div', { className: 'huit-footer-contact-block', key: 'email' },
              e('p', { className: 'huit-footer-contact-label' },
                e('span', { className: 'huit-footer-contact-icon', 'aria-hidden': 'true' }, svgIcon(emailPath, '16', '16')),
                'EMAIL',
              ),
              e('a', { href: `mailto:${HUIT_FOOTER_EMAIL}`, className: 'huit-footer-email' }, HUIT_FOOTER_EMAIL),
            ) : null,
          ),
          HUIT_FOOTER_SITEMAP_LINKS && HUIT_FOOTER_SITEMAP_LINKS.length > 0 ? e('div', { className: 'huit-footer-sitemap-container' },
            e('div', { className: 'huit-footer-sitemap' },
              e('p', { className: 'huit-footer-contact-label' }, 'SITEMAP'),
              e('ul', { className: 'huit-footer-sitemap-list' },
                ...HUIT_FOOTER_SITEMAP_LINKS.map((link) => e('li', { key: link.label },
                  e('a', { href: huitFooterHref(link.url), className: 'huit-footer-sitemap-link' }, link.label),
                )),
              ),
            ),
          ) : null,
        ),
      ),
    ),
  );
};
import ReactDOM from 'react-dom';
// components/HuitCourseOutlineCss.jsx

const HUIT_COURSE_OUTLINE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');

  /* Scoped layout customization for the Course Outline Tab */

  /* Header styling reverted to default */

  /* Hide default title row and space it nicely */
  .course-outline-tab {
    margin-top: 15px !important;
  }
  
  /* Hide the heading element */
  div[data-learner-type] {
    display: none !important;
  }

  /* Hide the default start/resume card in the main column */
  [data-testid="start-resume-card"] {
    display: none !important;
  }

  /* Course Banner layout */
  .huit-outline-banner {
    background: linear-gradient(135deg, #eef5fc 0%, #dbe9f9 100%) !important;
    border: 1px solid #c9dff7 !important;
    border-radius: 16px !important;
    padding: 30px 40px !important;
    margin-bottom: 25px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    position: relative !important;
    overflow: hidden !important;
    box-shadow: 0 4px 15px rgba(3, 78, 162, 0.04) !important;
  }

  .huit-outline-banner-left {
    flex: 1 !important;
    z-index: 2 !important;
    max-width: 65% !important;
  }

  .huit-outline-banner-title {
    font-size: 2rem !important;
    font-weight: 800 !important;
    color: #034EA2 !important;
    margin: 0 !important;
    line-height: 1.25 !important;
    font-family: Roboto, Arial, Helvetica, sans-serif;
  }

  .huit-outline-banner-subtitle {
    font-size: 1rem !important;
    color: #4a5568 !important;
    margin-top: 12px !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
  }

  /* Cả 3 banner dùng chung ảnh môn học nên phải khớp nhau và khớp card ngoài
     danh sách: khung tỉ lệ 380/160, contain thay vì cover để không cắt xén.
     Giữ width cố định, height do tỉ lệ suy ra; ảnh lệch tỉ lệ chừa nền #f1f5f9
     giống hệt khung ảnh ngoài danh sách, không dùng trắng cho đỡ lệch màu. */
  .huit-outline-banner-right {
    width: 380px !important;
    height: auto !important;
    aspect-ratio: 380 / 160 !important;
    background-color: #f1f5f9 !important;
    background-size: contain !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
    border: 2px solid #ffffff !important;
    z-index: 1 !important;
  }

  @media (max-width: 768px) {
    .huit-outline-banner {
      padding: 20px !important;
      flex-direction: column-reverse !important;
      align-items: stretch !important;
    }
    .huit-outline-banner-left {
      max-width: 100% !important;
      margin-top: 15px !important;
      text-align: center !important;
    }
    .huit-outline-banner-right {
      width: 180px !important;
      height: auto !important;
      margin: 0 auto !important;
    }
  }

  /* Sidebar styling and flex layout order */
  .course-outline-tab .col-md-4 {
    display: flex !important;
    flex-direction: column !important;
  }

  /* Order of widgets in the sidebar */
  .course-outline-tab .col-md-4 > :nth-child(1) { order: 1 !important; } /* ProctoringInfoPanel */
  

  
  /* Important Dates (CourseDates) Card style */
  .course-outline-tab .col-md-4 > section:has(#courseHome-dates) {
    order: 3 !important;
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 14px !important;
    padding: 24px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
    margin-bottom: 24px !important;
  }

  /* Course Tools Card style */
  .course-outline-tab .col-md-4 > section:has(a[href*="bookmark"]),
  .course-outline-tab .col-md-4 > section:has(#courseHome-launchTourLink) {
    order: 4 !important;
    background: #ffffff !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 14px !important;
    padding: 24px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
    margin-bottom: 24px !important;
  }

  .course-outline-tab .col-md-4 > section:not(:has(#courseHome-dates)):not(:has(a[href*="bookmark"])):not(:has(#courseHome-launchTourLink)) {
    order: 6 !important;
  }

  /* Sidebar widgets heading styling */
  .course-outline-tab .col-md-4 h2 {
    font-size: 1.15rem !important;
    font-weight: 700 !important;
    color: #1a202c !important;
    margin-bottom: 16px !important;
    border-bottom: 2px solid #edf2f7 !important;
    padding-bottom: 10px !important;
    font-family: Roboto, Arial, Helvetica, sans-serif;
  }

  /* List item improvements */
  .course-outline-tab .col-md-4 ul li,
  .course-outline-tab .col-md-4 ol li {
    margin-bottom: 12px !important;
  }

  .course-outline-tab .col-md-4 ul li a,
  .course-outline-tab .col-md-4 ol li a {
    color: #4a5568 !important;
    text-decoration: none !important;
    font-weight: 500 !important;
    font-size: 0.95rem !important;
    transition: color 0.15s ease !important;
  }

  .course-outline-tab .col-md-4 ul li a:hover,
  .course-outline-tab .col-md-4 ol li a:hover {
    color: #034EA2 !important;
  }

  /* Hide Handouts card */
  .course-outline-tab .col-md-4 > section:has(iframe[title*="phát tay"]),
  .course-outline-tab .col-md-4 > section:has(iframe[title*="Handouts"]),
  .course-outline-tab .col-md-4 > section:has(iframe[title*="handouts"]) {
    display: none !important;
  }

  /* Tools Button Container */
  .huit-tools-btn-container {
    margin-top: 16px !important;
    border-top: 1px solid #edf2f7 !important;
    padding-top: 16px !important;
  }

  .huit-btn-resume-sidebar {
    display: block !important;
    width: 100% !important;
    background-color: #034EA2 !important;
    color: #ffffff !important;
    text-align: center !important;
    padding: 12px 20px !important;
    border-radius: 8px !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    text-decoration: none !important;
    box-shadow: 0 4px 10px rgba(3, 78, 162, 0.15) !important;
    transition: background-color 0.15s ease, transform 0.1s ease !important;
  }

  .huit-btn-resume-sidebar:hover {
    background-color: #023c7d !important;
    color: #ffffff !important;
    text-decoration: none !important;
    transform: translateY(-1px) !important;
  }

  .huit-btn-resume-sidebar:active {
    transform: translateY(0) !important;
  }

  .huit-btn-group-sidebar {
    display: block !important;
    width: 100% !important;
    background-color: #ffffff !important;
    color: #034EA2 !important;
    border: 2px solid #034EA2 !important;
    text-align: center !important;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    font-weight: 700 !important;
    font-size: 0.95rem !important;
    text-decoration: none !important;
    box-shadow: 0 2px 6px rgba(3, 78, 162, 0.08) !important;
    transition: all 0.15s ease !important;
  }

  .huit-btn-group-sidebar:hover {
    background-color: #034EA2 !important;
    color: #ffffff !important;
    text-decoration: none !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 10px rgba(3, 78, 162, 0.2) !important;
  }

  .huit-btn-group-sidebar:active {
    transform: translateY(0) !important;
  }

  .huit-tab-group-assignment {
    color: #454545 !important;
    border-bottom: 2px solid transparent !important;
    text-decoration: none !important;
    cursor: pointer !important;
    transition: color 0.15s ease, border-color 0.15s ease !important;
  }

  .huit-tab-group-assignment:hover {
    color: #034EA2 !important;
    border-bottom: 2px solid #034EA2 !important;
    text-decoration: none !important;
  }

  /* Main section accordion styling */
  .course-outline-tab .col-md-8 .card {
    border: 1px solid #e2e8f0 !important;
    border-radius: 12px !important;
    margin-bottom: 16px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) !important;
    transition: box-shadow 0.2s ease !important;
  }

  .course-outline-tab .col-md-8 .card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05) !important;
  }

  .course-outline-tab .col-md-8 .card-header {
    background-color: #ffffff !important;
    padding: 16px 24px !important;
    border-bottom: 1px solid #edf2f7 !important;
  }

  .course-outline-tab .col-md-8 .card-header button {
    color: #1a202c !important;
    font-weight: 700 !important;
    font-size: 1.05rem !important;
    text-decoration: none !important;
  }

  /* Custom Progress Banner Styles */
  .huit-progress-banner {
    background: linear-gradient(135deg, #eef5fc 0%, #dbe9f9 100%) !important;
    border: 1px solid #c9dff7 !important;
    border-radius: 16px !important;
    padding: 30px 40px !important;
    margin-bottom: 25px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    position: relative !important;
    overflow: hidden !important;
    box-shadow: 0 4px 15px rgba(3, 78, 162, 0.04) !important;
  }

  .huit-progress-banner-left {
    flex: 1 !important;
    max-width: 45% !important;
    z-index: 2 !important;
  }

  .huit-progress-banner-title {
    font-size: 2rem !important;
    font-weight: 800 !important;
    color: #034EA2 !important;
    margin: 0 !important;
    line-height: 1.25 !important;
    font-family: Roboto, Arial, Helvetica, sans-serif;
  }

  .huit-progress-banner-subtitle {
    font-size: 1rem !important;
    color: #4a5568 !important;
    margin-top: 12px !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
  }

  .huit-progress-banner-middle {
    display: flex !important;
    flex-direction: column !important;
    gap: 10px !important;
    min-width: 260px !important;
    z-index: 2 !important;
  }

  .huit-progress-banner-logo {
    width: 380px !important;
    height: auto !important;
    aspect-ratio: 380 / 160 !important;
    background-color: #f1f5f9 !important;
    background-size: contain !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
    border: 2px solid #ffffff !important;
    z-index: 1 !important;
    flex-shrink: 0 !important;
    margin: 0 20px !important;
  }

  .huit-progress-btn-row {
    display: flex !important;
    gap: 10px !important;
    width: 100% !important;
  }

  .huit-progress-btn {
    flex: 1 !important;
    background-color: #034EA2 !important;
    color: #ffffff !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 10px 16px !important;
    font-size: 0.9rem !important;
    font-weight: 700 !important;
    text-align: center !important;
    text-decoration: none !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease, transform 0.1s ease !important;
    box-shadow: 0 2px 6px rgba(3, 78, 162, 0.15) !important;
  }

  .huit-progress-btn:hover {
    background-color: #023c7d !important;
    color: #ffffff !important;
    text-decoration: none !important;
  }

  .huit-progress-btn:active {
    transform: scale(0.98) !important;
  }

  .huit-progress-btn-print {
    width: 100% !important;
  }

  .huit-progress-toast {
    position: fixed !important;
    top: 24px !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    background-color: #2d3748 !important;
    color: #ffffff !important;
    padding: 12px 24px !important;
    border-radius: 8px !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    z-index: 9999 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    animation: huit-fade-in-out 3s forwards !important;
  }

  @keyframes huit-fade-in-out {
    0% { opacity: 0; transform: translate(-50%, -20px); }
    10% { opacity: 1; transform: translate(-50%, 0); }
    90% { opacity: 1; transform: translate(-50%, 0); }
    100% { opacity: 0; transform: translate(-50%, -20px); }
  }

  @media (max-width: 992px) {
    .huit-progress-banner {
      flex-direction: column !important;
      padding: 24px !important;
      text-align: center !important;
      gap: 20px !important;
    }
    .huit-progress-banner-left {
      max-width: 100% !important;
      order: 2 !important;
    }
    .huit-progress-banner-logo {
      width: 180px !important;
      height: auto !important;
      margin: 0 auto !important;
      order: 1 !important;
    }
    .huit-progress-banner-middle {
      width: 100% !important;
      max-width: 320px !important;
      margin: 0 auto !important;
      order: 3 !important;
    }
  }

  /* Custom Dates Banner Styles */
  .huit-dates-banner {
    background: linear-gradient(135deg, #eef5fc 0%, #dbe9f9 100%) !important;
    border: 1px solid #c9dff7 !important;
    border-radius: 16px !important;
    padding: 30px 40px !important;
    margin-bottom: 25px !important;
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    position: relative !important;
    overflow: hidden !important;
    box-shadow: 0 4px 15px rgba(3, 78, 162, 0.04) !important;
  }

  .huit-dates-banner-left {
    flex: 1 !important;
    z-index: 2 !important;
    max-width: 65% !important;
  }

  .huit-dates-banner-title {
    font-size: 2rem !important;
    font-weight: 800 !important;
    color: #034EA2 !important;
    margin: 0 !important;
    line-height: 1.25 !important;
    font-family: Roboto, Arial, Helvetica, sans-serif;
  }

  .huit-dates-banner-subtitle {
    font-size: 1rem !important;
    color: #4a5568 !important;
    margin-top: 12px !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
  }

  .huit-dates-banner-right {
    width: 380px !important;
    height: auto !important;
    aspect-ratio: 380 / 160 !important;
    background-color: #f1f5f9 !important;
    background-size: contain !important;
    background-repeat: no-repeat !important;
    background-position: center !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
    border: 2px solid #ffffff !important;
    z-index: 1 !important;
  }

  /* Hide only the description paragraph of the Dates page heading when custom banner is rendered */
  .huit-dates-banner-container + div[role="heading"][aria-level="1"] + p {
    display: none !important;
  }

  @media (max-width: 992px) {
    .huit-dates-banner {
      padding: 20px !important;
      flex-direction: column-reverse !important;
      align-items: stretch !important;
      text-align: center !important;
      gap: 20px !important;
    }
    .huit-dates-banner-left {
      max-width: 100% !important;
      margin-top: 15px !important;
      text-align: center !important;
    }
    .huit-dates-banner-right {
      width: 180px !important;
      height: auto !important;
      margin: 0 auto !important;
    }
  }
`;


// HUIT MFE Learner Dashboard Branding — CSS Definitions.
// Injected as a <style> tag via HuitLearnerDashboardSlots.
const HUIT_LEARNER_DASHBOARD_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');

  body { overflow-x: hidden !important; }
  
  /* Ẩn các thành phần không mong muốn */
  .course-list-heading-container, .pgn__page-header, .masquerade-bar { display: none !important; }
  
  /* Mở rộng cột nội dung chính và ẩn cột sidebar */
  .course-list-column { flex: 0 0 100% !important; max-width: 100% !important; padding: 0 !important; }
  .sidebar-column { display: none !important; }
  
  /* XÓA KHOẢNG TRỐNG TRÊN BANNER: Reset padding của các container cha */
  main, .pgn__page-wrapper { padding-top: 0 !important; margin-top: 0 !important; }
  .container-mw-xl { max-width: 1400px !important; margin: 0 auto !important; padding: 0 20px !important; }
  
  /* BANNER: Full-width an toàn, không bị cắt xén */
  .huit-fullwidth-banner {
    width: 100vw !important; 
    position: relative !important; 
    left: 50% !important;
    transform: translateX(-50%) !important; 
    margin-bottom: 40px !important; 
    display: block;
  }
  .huit-fullwidth-banner img { 
    width: 100% !important; 
    height: auto !important; 
    display: block !important; 
  }

  /* GRID: Ép cứng 4 CỘT TRÊN MỘT HÀNG (Dành cho Desktop) */
  .huit-grid { 
      display: grid; 
      grid-template-columns: repeat(4, 1fr); 
      gap: 24px; 
      padding-bottom: 40px; 
  }
  
  /* Responsive: Tự động thu gọn số cột trên các màn hình nhỏ */
  @media (max-width: 1200px) { .huit-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 900px) { .huit-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 600px) { .huit-grid { grid-template-columns: 1fr; } }

  /* Card Khóa Học */
  .huit-card {
    border: 1px solid #e0e0e0; border-radius: 12px; overflow: hidden; background-color: #fff;
    display: flex; flex-direction: column; box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease; height: 100%;
  }
  .huit-card:hover { transform: translateY(-5px); box-shadow: 0 8px 24px rgba(0,0,0,0.12); }
  /* Khung tỉ lệ 380/160 dùng chung cho mọi chỗ hiện ảnh môn học; contain thay
     cho cover vì giảng viên upload đủ mọi tỉ lệ, cover cắt mất chữ trên banner.
     Tỉ lệ cố định giữ lưới card không so le, phần thừa lộ nền #f1f5f9 — cùng
     màu với khung ảnh ở danh sách, trang chi tiết và banner trong khoá học. */
  .huit-card-img { width: 100%; height: auto; aspect-ratio: 380 / 160; object-fit: contain; border-bottom: 1px solid #f0f0f0; background-color: #f1f5f9; }
  .huit-card-content { padding: 20px; display: flex; flex-direction: column; flex: 1; }
  .huit-card-title { font-size: 1.15rem; font-weight: 800; color: #1a1a1a; margin: 0 0 15px 0; line-height: 1.4; min-height: 2.8em; }

  /* Thanh tiến độ */
  .progress-bg { width: 100%; background-color: #e9ecef; border-radius: 10px; height: 8px; margin-bottom: 10px; overflow: hidden; }
  .progress-fill { height: 100%; border-radius: 10px; background-color: #004d99; transition: width 1s ease-in-out; }
  .progress-text { font-size: 0.8rem; color: #666; font-weight: 600; text-align: right; margin-bottom: 15px; }

  /* Nút Footer */
  .huit-footer-row { margin-top: auto; display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #f0f0f0; padding-top: 15px; }
  .huit-btn-go { background-color: #004d99; color: #fff !important; padding: 8px 18px; border-radius: 6px; text-decoration: none !important; font-weight: bold; font-size: 0.9rem; }
  .huit-btn-go:hover { background-color: #003366; }
`;


async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];

  const messages = {
    "footer.poweredby.text": {
      id: "footer.poweredby.text",
      defaultMessage: "Powered by",
      description: "text for the footer",
    },
    "footer.tutorlogo.altText": {
      id: "footer.tutorlogo.altText",
      defaultMessage: "Runs on Tutor",
      description: "alt text for the footer tutor logo",
    },
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: "Powered by Open edX",
      description: "alt text for the footer logo.",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `Copyrights ©${new Date().getFullYear()}. All Rights Reserved.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>{intl.formatMessage(messages["footer.poweredby.text"])}</li>
              <li>
                <a
                  href="https://edly.io/tutor/"
                  rel="noreferrer"
                  target="_blank"
                >
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/tutor-logo.png`}
                    alt={intl.formatMessage(
                      messages["footer.tutorlogo.altText"]
                    )}
                    width="57"
                  />
                </a>
              </li>
              <li>
                <a href="https://open.edx.org" rel="noreferrer" target="_blank">
                  <img
                    src={`${config.LMS_BASE_URL}/theming/asset/images/openedx-logo.png`}
                    alt={intl.formatMessage(messages["footer.logo.altText"])}
                    width="79"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {indigoFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a href={`${config.LMS_BASE_URL}${link.url}`}>{link.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
      </footer>
    </div>
  );
};

const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookie = 'selected-paragon-theme-variant';
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCookie(themeCookie) === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    window.localStorage.setItem(themeCookie, theme);
    setTimeout(() => {
      setCookie(themeCookie, theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  useEffect(() => {
    if (!getCookie(themeCookie) || getCookie(themeCookie) === 'undefined') {
      return;
    }
    if (getCookie(themeCookie) !== window.localStorage.getItem(themeCookie)) {
      window.localStorage.setItem(themeCookie, getCookie(themeCookie));
      window.location.reload();
    }
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            defaultChecked={getCookie(themeCookie) === "dark"}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${
            isDarkThemeEnabled ? "Light" : "Dark"
          } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};

const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "Mobile view header logo altText",
    },
  };
  return (
    <div className="container-xl py-2 d-flex align-items-center justify-content-between">
      <a
        className="logo"
        href={`${config.LMS_BASE_URL}/dashboard`}
        style={Object.assign({}, { display: "flex", alignItems: "center" })}
      >
        <img
          className="d-block"
          src={`${config.LMS_BASE_URL}/theming/asset/images/logo.png`}
          alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])}
          height={21}
        />
      </a>
      <ToggleThemeButton />
    </div>
  );
};

// HUIT MFE Header Branding — slot wiring (runtime patch).
// Components and CSS string are defined in mfe-env-config-buildtime-definitions.
// DIRECT_PLUGIN and PLUGIN_OPERATIONS are available from the FPF dynamic import above.
// React, HUIT_HEADER_CSS, HuitLogoLegacy are available from module-level scope (buildtime-definitions).

// [F] CSS inject → Studio header slots (frontend-app-authoring / Studio MFE).
// Different @edx/frontend-component-header versions expose different Studio slots.
// Keep both so the style is rendered on Ulmo and newer header builds.
// addPlugins(config, 'org.openedx.frontend.layout.studio_header_actions.v1', [
//   {
//     op: PLUGIN_OPERATIONS.Insert,
//     widget: {
//       id: 'huit_studio_header_css',
//       type: DIRECT_PLUGIN,
//       priority: 1,
//       RenderWidget: () => React.createElement('style', { key: 'huit-studio-css' }, HUIT_HEADER_CSS),
//     },
//   },
// ]);

addPlugins(config, 'org.openedx.frontend.layout.studio_header_search_button_slot.v1', [
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_studio_header_search_css',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: () => React.createElement('style', { key: 'huit-studio-search-css' }, HUIT_HEADER_CSS),
    },
  },
]);

const HuitCourseNavTabInjector = () => {
  React.useEffect(() => {
    const getCourseId = () => {
      const m = window.location.pathname.match(/\/learning\/course\/([^\/]+)/) ||
                window.location.pathname.match(/\/discussions\/([^\/]+)/) ||
                window.location.pathname.match(/\/courses?\/([^\/]+)/);
      return m ? m[1] : null;
    };

    const ltiBase = (window.location.hostname === 'local.openedx.io' || window.location.hostname === 'apps.local.openedx.io' || window.location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://lti-groups.huit.edu.vn';

    const ensureNavTab = () => {
      if (window.location.pathname.includes('/instructor')) return;
      const cId = getCourseId();
      if (!cId) return;

      const targetUrl = `${ltiBase}/auth/openedxsso/login?course_id=${encodeURIComponent(cId)}`;

      const tabContainers = document.querySelectorAll(
        'nav.nav-tabs, .nav-tabs, nav.course-tabs, .course-tabs-navigation nav, nav[aria-label*="Course" i], nav[aria-label*="Điều hướng" i], nav.navigation-tabs, .learning-header nav, nav.course-material, ol.course-tabs, ul.course-tabs'
      );

      for (let i = 0; i < tabContainers.length; i++) {
        const container = tabContainers[i];
        if (container.querySelector('#huit-nav-group-assignment')) continue;

        const links = container.querySelectorAll('a');
        let isCourseTabs = false;
        for (let j = 0; j < links.length; j++) {
          const a = links[j];
          const href = a.getAttribute('href') || '';
          const text = (a.textContent || '').trim().toLowerCase();
          if (
            href.includes('/course') || href.includes('/progress') || href.includes('/dates') || href.includes('/discussion') || href.includes('/instructor') || href.includes('/home') ||
            text.includes('khóa học') || text.includes('tiến triển') || text.includes('ngày') || text.includes('cuộc thảo luận') || text.includes('người hướng dẫn') || text.includes('instructor')
          ) {
            isCourseTabs = true;
            break;
          }
        }

        if (!isCourseTabs && !container.classList.contains('nav-tabs') && !container.classList.contains('course-tabs')) continue;

        const ul = container.tagName.toLowerCase() === 'ul' || container.tagName.toLowerCase() === 'ol' ? container : container.querySelector('ul, ol');

        const tabLink = document.createElement('a');
        tabLink.id = 'huit-nav-group-assignment';
        tabLink.className = 'nav-item flex-shrink-0 nav-link huit-tab-group-assignment';
        tabLink.href = targetUrl;
        tabLink.target = '_blank';
        tabLink.setAttribute('role', 'tab');
        tabLink.setAttribute('tabindex', '0');
        tabLink.textContent = 'Bài tập nhóm';

        if (ul) {
          const li = document.createElement('li');
          li.id = 'huit-nav-group-assignment-li';
          li.className = 'nav-item tab';
          li.appendChild(tabLink);
          ul.appendChild(li);
        } else {
          container.appendChild(tabLink);
        }
      }
    };

    ensureNavTab();
    const intervalId = setInterval(ensureNavTab, 300);
    return () => clearInterval(intervalId);
  }, []);

  return null;
};

// [A] CSS inject + HUIT logo → header logo slot.
// Chỉ đăng ký dưới key alias `logo_slot`: FPF resolve slot bằng findLast trên
// Object.keys(pluginSlots), nên thêm key `org.openedx.frontend.layout.header_logo.v1`
// sau key này sẽ đảo ngược config nào thắng.
addPlugins(config, 'logo_slot', [
  // {
  //   op: PLUGIN_OPERATIONS.Modify,
  //   widgetId: 'default_contents',
  //   fn: (widget) => {
  //     widget.content.href = 'https://lms.huit.edu.vn';
  //     return widget;
  //   },
  // },
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_header_css_legacy',
      type: DIRECT_PLUGIN,
      priority: 1,
      RenderWidget: () => React.createElement('style', { key: 'huit-css-legacy' }, HUIT_HEADER_CSS),
    },
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_logo_legacy',
      type: DIRECT_PLUGIN,
      priority: 10,
      RenderWidget: HuitLogoLegacy,
    },
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_nav_tab_universal_widget',
      type: DIRECT_PLUGIN,
      priority: 20,
      RenderWidget: HuitCourseNavTabInjector,
    },
  },
]);

// tutor-indigo chèn widget `custom_logo` ở vòng iter_mfes() chạy sau file này, nên
// op Hide khai báo tại đây sẽ không tìm thấy widget. Chặn ngay tại lúc addPlugins() push.
const huitLogoSlotPlugins = config.pluginSlots['logo_slot'].plugins;
const huitLogoSlotPush = huitLogoSlotPlugins.push.bind(huitLogoSlotPlugins);
Object.defineProperty(huitLogoSlotPlugins, 'push', {
  enumerable: false,
  writable: true,
  configurable: true,
  value: (...incoming) => huitLogoSlotPush(
    ...incoming.filter(
      (plugin) => !(plugin.op === PLUGIN_OPERATIONS.Insert && plugin.widget?.id === 'custom_logo'),
    ),
  ),
});

addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
]);

// [C+D] HUIT nav links — Desktop + Mobile main menus (standard header slots)
// const huitMenuItems = [{"label": "Trang ch\u1ee7", "url": "/"}, {"label": "Ch\u01b0\u01a1ng tr\u00ecnh", "url": "/dashboard/programs"}, {"label": "Tr\u1ee3 gi\u00fap", "url": "/help"}].map(
//   ({ label, url }) => ({ type: 'item', href: url, content: label })
// );
// addPlugins(config, 'org.openedx.frontend.layout.header_desktop_main_menu.v1', [
//   {
//     op: PLUGIN_OPERATIONS.Modify,
//     widgetId: 'default_contents',
//     fn: (widget) => { widget.content.menu = huitMenuItems; return widget; },
//   },
// ]);
// addPlugins(config, 'org.openedx.frontend.layout.header_mobile_main_menu.v1', [
//   {
//     op: PLUGIN_OPERATIONS.Modify,
//     widgetId: 'default_contents',
//     fn: (widget) => { widget.content.menu = huitMenuItems; return widget; },
//   },
// ]);

// [E] Logged-out items — "Đăng nhập / Đăng ký" (standard header slots)
// keepDefault: false replaces defaults to avoid duplicates.
// config.pluginSlots['org.openedx.frontend.layout.header_desktop_logged_out_items.v1'] = {
//   keepDefault: false,
//   plugins: [{
//     op: PLUGIN_OPERATIONS.Insert,
//     widget: { id: 'huit_desktop_logged_out', type: DIRECT_PLUGIN, RenderWidget: HuitDesktopLoggedOut },
//   }],
// };
// config.pluginSlots['org.openedx.frontend.layout.header_mobile_logged_out_items.v1'] = {
//   keepDefault: false,
//   plugins: [{
//     op: PLUGIN_OPERATIONS.Insert,
//     widget: { id: 'huit_mobile_logged_out', type: DIRECT_PLUGIN, RenderWidget: HuitMobileLoggedOut },
//   }],
// };
// HUIT MFE Footer — slot wiring (runtime patch).
// HuitFooter and DIRECT_PLUGIN / PLUGIN_OPERATIONS are in outer env.config.jsx scope.

// Replace the default MFE footer with HUIT-branded footer (standard FooterSlot).
addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_footer',
      type: DIRECT_PLUGIN,
      priority: 10,
      RenderWidget: HuitFooter,
    },
  },
]);

// Legacy alias: footer_slot / footer_plugin_slot (older MFE versions).
addPlugins(config, 'footer_slot', [
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_footer_legacy',
      type: DIRECT_PLUGIN,
      priority: 10,
      RenderWidget: HuitFooter,
    },
  },
]);

// Replace the Studio footer with the same HUIT-branded footer.
addPlugins(config, 'org.openedx.frontend.layout.studio_footer.v1', [
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_studio_footer',
      type: DIRECT_PLUGIN,
      priority: 10,
      RenderWidget: HuitFooter,
    },
  },
]);

// Legacy alias: studio_footer_slot (older MFE versions).
addPlugins(config, 'studio_footer_slot', [
  {
    op: PLUGIN_OPERATIONS.Hide,
    widgetId: 'default_contents',
  },
  {
    op: PLUGIN_OPERATIONS.Insert,
    widget: {
      id: 'huit_studio_footer_legacy',
      type: DIRECT_PLUGIN,
      priority: 10,
      RenderWidget: HuitFooter,
    },
  },
]);
// components/HuitCourseOutlineSlots.jsx
// DIRECT_PLUGIN and PLUGIN_OPERATIONS are available from the FPF dynamic import above.
// React, HUIT_COURSE_OUTLINE_CSS, and bannerIllustration are available from module-level scope.

const huitOutlineDefaultBanner = "https://files.lms.huit.edu.vn/openedx/openedx/media/course-default-banner.png";

if (process.env.APP_ID === 'learning') {
  addPlugins(config, 'org.openedx.frontend.learning.course_outline_tab_notifications.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'huit_course_outline_sidebar_v1',
        type: DIRECT_PLUGIN,
        RenderWidget: ({ courseId }) => {
          const LMS_BASE = process.env.LMS_BASE_URL || window.location.origin.replace('apps.', '');
          
          const [resumeData, setResumeData] = React.useState(null);
          const [courseTitle, setCourseTitle] = React.useState('Khóa học');
          const [courseImage, setCourseImage] = React.useState(huitOutlineDefaultBanner);
          const [portalTarget, setPortalTarget] = React.useState(null);
          const [toolsPortalTarget, setToolsPortalTarget] = React.useState(null);

          React.useEffect(() => {
            if (!courseId) return;

            // 1. Fetch resume URL and details from outline API
            fetch(`${LMS_BASE}/api/course_home/outline/${courseId}`, { credentials: 'include' })
              .then(res => {
                if (!res.ok) throw new Error("Outline API error");
                return res.json();
              })
              .then(data => {
                if (data && data.resume_course) {
                  setResumeData({
                    url: data.resume_course.url,
                    hasVisited: data.resume_course.has_visited_course
                  });
                }
              })
              .catch(err => console.log("Lỗi tải API Outline:", err));

            // 2. Fetch course avatar from Courses API
            fetch(`${LMS_BASE}/api/courses/v1/courses/${courseId}`, { credentials: 'include' })
              .then(res => {
                if (!res.ok) throw new Error("Courses API error");
                return res.json();
              })
              .then(data => {
                if (data && data.media && data.media.course_image && data.media.course_image.uri) {
                  const imgUrl = data.media.course_image.uri.startsWith('http')
                    ? data.media.course_image.uri
                    : `${LMS_BASE}${data.media.course_image.uri}`;
                  
                  const img = new Image();
                  img.src = imgUrl;
                  img.onload = () => setCourseImage(imgUrl);
                  img.onerror = () => setCourseImage(huitOutlineDefaultBanner);
                } else {
                  setCourseImage(huitOutlineDefaultBanner);
                }
              })
              .catch(err => {
                console.log("Lỗi tải API Courses:", err);
                setCourseImage(huitOutlineDefaultBanner);
              });

            // 3. Extract course title from DOM heading
            const titleEl = document.querySelector('div[role="heading"][aria-level="1"]');
            if (titleEl) {
              setCourseTitle(titleEl.textContent);
            }

            // Function to dynamically check and mount portal targets
            const mountTargets = () => {
              // 4. Create banner portal container inside the main column (.col-md-8)
              // We remove the .col-12 fallback to ensure it sits inside the left column and doesn't span full width.
              const mainCol = document.querySelector('.course-outline-tab .col-md-8, .course-outline-tab .col-lg-8');
              if (mainCol) {
                let bannerContainer = document.querySelector('.huit-banner-portal-container');
                if (!bannerContainer) {
                  bannerContainer = document.createElement('div');
                  bannerContainer.className = 'w-100 p-0 mb-4 huit-banner-portal-container';
                  mainCol.insertBefore(bannerContainer, mainCol.firstChild);
                }
                setPortalTarget(bannerContainer);
              }

              // 5. Create portal container inside the Course Tools card (detect by bookmark link or launch tour link)
              const sections = document.querySelectorAll('.course-outline-tab .col-md-4 > section');
              let foundToolsCard = null;
              for (const sec of sections) {
                if (sec.querySelector('a[href*="bookmark"]') || sec.querySelector('#courseHome-launchTourLink')) {
                  foundToolsCard = sec;
                  break;
                }
              }
              if (foundToolsCard) {
                let btnContainer = foundToolsCard.querySelector('.huit-tools-btn-container');
                if (!btnContainer) {
                  btnContainer = document.createElement('div');
                  btnContainer.className = 'huit-tools-btn-container mt-3';
                  foundToolsCard.appendChild(btnContainer);
                }
                setToolsPortalTarget(btnContainer);
              }

              // 6. Inject "Bài tập nhóm" tab into the Course Navigation Bar
              if (courseId && !window.location.pathname.includes('/instructor')) {
                const ltiBase = (window.location.hostname === 'local.openedx.io' || window.location.hostname === 'apps.local.openedx.io' || window.location.hostname === 'localhost')
                  ? 'http://localhost:3000'
                  : 'https://lti-groups.huit.edu.vn';
                const targetUrl = `${ltiBase}/auth/openedxsso/login?course_id=${encodeURIComponent(courseId)}`;

                const tabContainers = document.querySelectorAll(
                  'nav.nav-tabs, .nav-tabs, nav.course-tabs, .course-tabs-navigation nav, nav[aria-label*="Course" i], nav[aria-label*="Điều hướng" i], nav.navigation-tabs, .learning-header nav'
                );

                tabContainers.forEach(container => {
                  if (container.querySelector('#huit-nav-group-assignment')) return;

                  const links = container.querySelectorAll('a');
                  let isCourseTabs = false;
                  links.forEach(a => {
                    const href = a.getAttribute('href') || '';
                    const text = (a.textContent || '').trim().toLowerCase();
                    if (
                      href.includes('/course') || href.includes('/progress') || href.includes('/dates') || href.includes('/discussion') || href.includes('/instructor') ||
                      text.includes('khóa học') || text.includes('tiến triển') || text.includes('ngày') || text.includes('cuộc thảo luận') || text.includes('người hướng dẫn')
                    ) {
                      isCourseTabs = true;
                    }
                  });

                  if (!isCourseTabs && !container.classList.contains('nav-tabs')) return;

                  const ul = container.tagName.toLowerCase() === 'ul' ? container : container.querySelector('ul');

                  const tabLink = document.createElement('a');
                  tabLink.id = 'huit-nav-group-assignment';
                  tabLink.className = 'nav-item flex-shrink-0 nav-link huit-tab-group-assignment';
                  tabLink.href = targetUrl;
                  tabLink.target = '_blank';
                  tabLink.setAttribute('role', 'tab');
                  tabLink.setAttribute('tabindex', '0');
                  tabLink.textContent = 'Bài tập nhóm';

                  if (ul) {
                    const li = document.createElement('li');
                    li.id = 'huit-nav-group-assignment-li';
                    li.className = 'nav-item';
                    li.appendChild(tabLink);
                    ul.appendChild(li);
                  } else {
                    container.appendChild(tabLink);
                  }
                });
              }
            };

            mountTargets();
            const intervalId = setInterval(mountTargets, 500);

            // Cleanup portal container on unmount
            return () => {
              clearInterval(intervalId);
              const bannerContainer = document.querySelector('.huit-banner-portal-container');
              if (bannerContainer && bannerContainer.parentNode) {
                bannerContainer.parentNode.removeChild(bannerContainer);
              }
              const toolsBtnContainer = document.querySelector('.huit-tools-btn-container');
              if (toolsBtnContainer && toolsBtnContainer.parentNode) {
                toolsBtnContainer.parentNode.removeChild(toolsBtnContainer);
              }
            };
          }, [courseId, LMS_BASE]);

          // Render the banner inside the Portal
          const rightStyle = { backgroundImage: `url(${courseImage})` };
          const bannerNode = portalTarget && ReactDOM.createPortal(
            <div className="huit-outline-banner">
              <div className="huit-outline-banner-left">
                <h1 className="huit-outline-banner-title">{courseTitle}</h1>
                <div className="huit-outline-banner-subtitle">Chào mừng bạn quay trở lại! Hãy tiếp tục hành trình học tập.</div>
              </div>
              <div 
                className="huit-outline-banner-right"
                style={rightStyle}
              ></div>
            </div>,
            portalTarget
          );

          // Render the Resume Button inside the Course Tools Card via Portal
          const toolsNode = resumeData && resumeData.url && toolsPortalTarget
            ? ReactDOM.createPortal(
                <a href={resumeData.url} className="huit-btn-resume-sidebar">
                  {resumeData.hasVisited ? 'Tiếp tục khóa học' : 'Bắt đầu khóa học'}
                </a>,
                toolsPortalTarget
              )
            : null;

          return (
            <React.Fragment>
              <style>{HUIT_COURSE_OUTLINE_CSS}</style>
              {bannerNode}
              {toolsNode}
            </React.Fragment>
          );
        }
      }
    }
  ]);

  addPlugins(config, 'org.openedx.frontend.learning.progress_tab_certificate_status_main_body.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'huit_course_progress_banner_v1',
        type: DIRECT_PLUGIN,
        RenderWidget: () => {
          const LMS_BASE = process.env.LMS_BASE_URL || window.location.origin.replace('apps.', '');
          
          const [courseId, setCourseId] = React.useState(null);
          const [courseTitle, setCourseTitle] = React.useState('Khóa học');
          const [courseImage, setCourseImage] = React.useState(huitOutlineDefaultBanner);
          const [certData, setCertData] = React.useState(null);
          const [portalTarget, setPortalTarget] = React.useState(null);
          const [toastMessage, setToastMessage] = React.useState(null);

          React.useEffect(() => {
            const match = window.location.pathname.match(/\/courses?\/([^\/]+)\/progress/);
            if (match) {
              setCourseId(match[1]);
            }
          }, [window.location.pathname]);

          React.useEffect(() => {
            if (!courseId) return;

            // 1. Fetch course details & image
            fetch(`${LMS_BASE}/api/courses/v1/courses/${courseId}`, { credentials: 'include' })
              .then(res => {
                if (!res.ok) throw new Error("Courses API error");
                return res.json();
              })
              .then(data => {
                if (data) {
                  if (data.name) {
                    setCourseTitle(data.name);
                  }
                  if (data.media && data.media.course_image && data.media.course_image.uri) {
                    const imgUrl = data.media.course_image.uri.startsWith('http')
                      ? data.media.course_image.uri
                      : `${LMS_BASE}${data.media.course_image.uri}`;
                    
                    const img = new Image();
                    img.src = imgUrl;
                    img.onload = () => setCourseImage(imgUrl);
                    img.onerror = () => setCourseImage(huitOutlineDefaultBanner);
                  } else {
                    setCourseImage(huitOutlineDefaultBanner);
                  }
                }
              })
              .catch(err => {
                console.log("Lỗi tải API Courses:", err);
                setCourseImage(huitOutlineDefaultBanner);
              });

            // 2. Fetch progress details
            fetch(`${LMS_BASE}/api/course_home/progress/${courseId}`, { credentials: 'include' })
              .then(res => {
                if (!res.ok) throw new Error("Progress API error");
                return res.json();
              })
              .then(data => {
                if (data && data.certificate_data) {
                  setCertData({
                    status: data.certificate_data.cert_status,
                    url: data.certificate_data.cert_web_view_url,
                  });
                }
              })
              .catch(err => console.log("Lỗi tải API Progress:", err));

            // 3. Mount target above the progress header row in DOM
            const mountTarget = () => {
              const progressHeaderRow = document.querySelector('.row h1')?.closest('.row');
              if (progressHeaderRow && progressHeaderRow.parentNode) {
                let bannerContainer = document.querySelector('.huit-progress-banner-container');
                if (!bannerContainer) {
                  bannerContainer = document.createElement('div');
                  bannerContainer.className = 'w-100 p-0 mb-4 huit-progress-banner-container';
                  progressHeaderRow.parentNode.insertBefore(bannerContainer, progressHeaderRow);
                }
                setPortalTarget(bannerContainer);
              }
            };

            mountTarget();
            const intervalId = setInterval(mountTarget, 500);

            return () => {
              clearInterval(intervalId);
              const bannerContainer = document.querySelector('.huit-progress-banner-container');
              if (bannerContainer && bannerContainer.parentNode) {
                bannerContainer.parentNode.removeChild(bannerContainer);
              }
            };
          }, [courseId, LMS_BASE]);

          const handleViewCert = () => {
            if (certData && certData.status === 'downloadable' && certData.url) {
              window.open(`${LMS_BASE}${certData.url}`, '_blank');
            } else {
              showToast("Chứng chỉ của bạn đang được xử lý hoặc bạn chưa đủ điều kiện hoàn thành khóa học.");
            }
          };

          const handlePrintCert = () => {
            if (certData && certData.status === 'downloadable' && certData.url) {
              const printWindow = window.open(`${LMS_BASE}${certData.url}`, '_blank');
              if (printWindow) {
                printWindow.onload = () => {
                  printWindow.print();
                };
              }
            } else {
              showToast("Chứng chỉ của bạn đang được xử lý hoặc bạn chưa đủ điều kiện hoàn thành khóa học.");
            }
          };

          const handleShareProgress = () => {
            const shareUrl = window.location.href;
            const textToCopy = `Tôi đang tham gia khóa học "${courseTitle}". Xem tiến độ học tập của tôi tại đây: ${shareUrl}`;
            navigator.clipboard.writeText(textToCopy)
              .then(() => {
                showToast("Đã sao chép liên kết chia sẻ tiến độ học tập!");
              })
              .catch(err => {
                console.error("Lỗi clipboard:", err);
                showToast("Không thể sao chép liên kết.");
              });
          };

          const showToast = (msg) => {
            setToastMessage(msg);
            setTimeout(() => {
              setToastMessage(null);
            }, 3000);
          };

          const printIconStyle = { marginRight: '4px' };

          if (!portalTarget) return null;

          const logoStyle = { backgroundImage: `url(${courseImage})` };

          return ReactDOM.createPortal(
            <React.Fragment>
              <style>{HUIT_COURSE_OUTLINE_CSS}</style>
              <div className="huit-progress-banner">
                <div className="huit-progress-banner-left">
                  <h2 className="huit-progress-banner-title">Tiến độ học tập - {courseTitle}</h2>
                  <div className="huit-progress-banner-subtitle">Chào mừng bạn! Theo dõi và cập nhật tiến độ học tập của bạn tại đây.</div>
                </div>
                <div 
                  className="huit-progress-banner-logo"
                  style={logoStyle}
                ></div>
                <div className="huit-progress-banner-middle">
                  {/*
                  <div className="huit-progress-btn-row">
                    <button className="huit-progress-btn" onClick={handleViewCert}>
                      Xem chứng chỉ
                    </button>
                    <button className="huit-progress-btn" onClick={handleShareProgress}>
                      Chia sẻ tiến độ
                    </button>
                  </div>
                  <button className="huit-progress-btn huit-progress-btn-print" onClick={handlePrintCert}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={printIconStyle}>
                      <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <path d="M6 14h12v8H6z" />
                    </svg>
                    In chứng chỉ
                  </button>
                  */}
                  {toastMessage && (
                    <div className="huit-progress-toast">
                      {toastMessage}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>,
            portalTarget
          );
        }
      }
    }
  ]);

  addPlugins(config, 'org.openedx.frontend.layout.header_learning.v1', [
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'huit_course_dates_banner_v1',
        type: DIRECT_PLUGIN,
        RenderWidget: () => {
          const LMS_BASE = process.env.LMS_BASE_URL || window.location.origin.replace('apps.', '');
          const [courseId, setCourseId] = React.useState(null);
          const [courseTitle, setCourseTitle] = React.useState('Khóa học');
          const [courseImage, setCourseImage] = React.useState(huitOutlineDefaultBanner);
          const [portalTarget, setPortalTarget] = React.useState(null);

          React.useEffect(() => {
            const match = window.location.pathname.match(/\/courses?\/([^\/]+)\/dates/);
            if (match) {
              setCourseId(match[1]);
            } else {
              setCourseId(null);
              setPortalTarget(null);
            }
          }, [window.location.pathname]);

          React.useEffect(() => {
            if (!courseId) return;

            // 1. Fetch course details & image
            fetch(`${LMS_BASE}/api/courses/v1/courses/${courseId}`, { credentials: 'include' })
              .then(res => {
                if (!res.ok) throw new Error("Courses API error");
                return res.json();
              })
              .then(data => {
                if (data) {
                  if (data.name) {
                    setCourseTitle(data.name);
                  }
                  if (data.media && data.media.course_image && data.media.course_image.uri) {
                    const imgUrl = data.media.course_image.uri.startsWith('http')
                      ? data.media.course_image.uri
                      : `${LMS_BASE}${data.media.course_image.uri}`;
                    
                    const img = new Image();
                    img.src = imgUrl;
                    img.onload = () => setCourseImage(imgUrl);
                    img.onerror = () => setCourseImage(huitOutlineDefaultBanner);
                  } else {
                    setCourseImage(huitOutlineDefaultBanner);
                  }
                }
              })
              .catch(err => {
                console.log("Lỗi tải API Courses:", err);
                setCourseImage(huitOutlineDefaultBanner);
              });

            // 2. Mount target above the dates header row in DOM
            const mountTarget = () => {
              const datesHeading = document.querySelector('div[role="heading"][aria-level="1"]');
              if (datesHeading && datesHeading.parentNode) {
                let bannerContainer = document.querySelector('.huit-dates-banner-container');
                if (!bannerContainer) {
                  bannerContainer = document.createElement('div');
                  bannerContainer.className = 'w-100 p-0 mb-4 huit-dates-banner-container';
                  datesHeading.parentNode.insertBefore(bannerContainer, datesHeading);
                }
                setPortalTarget(bannerContainer);
              }
            };

            mountTarget();
            const intervalId = setInterval(mountTarget, 500);

            return () => {
              clearInterval(intervalId);
              const bannerContainer = document.querySelector('.huit-dates-banner-container');
              if (bannerContainer && bannerContainer.parentNode) {
                bannerContainer.parentNode.removeChild(bannerContainer);
              }
            };
          }, [courseId, LMS_BASE]);

          if (!portalTarget) return null;

          const logoStyle = { backgroundImage: `url(${courseImage})` };

          return ReactDOM.createPortal(
            <React.Fragment>
              <style>{HUIT_COURSE_OUTLINE_CSS}</style>
              <div className="huit-dates-banner">
                <div className="huit-dates-banner-left">
                  <h2 className="huit-dates-banner-title">{courseTitle}</h2>
                </div>
                <div 
                  className="huit-dates-banner-right"
                  style={logoStyle}
                ></div>
              </div>
            </React.Fragment>,
            portalTarget
          );
        }
      }
    },
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'huit_learning_course_nav_group_tab_v1',
        type: DIRECT_PLUGIN,
        RenderWidget: () => {
          React.useEffect(() => {
            const getCourseId = () => {
              const m = window.location.pathname.match(/\/learning\/course\/([^\/]+)/) ||
                        window.location.pathname.match(/\/courses?\/([^\/]+)/);
              return m ? m[1] : null;
            };

            const ltiBase = (window.location.hostname === 'local.openedx.io' || window.location.hostname === 'apps.local.openedx.io' || window.location.hostname === 'localhost')
              ? 'http://localhost:3000'
              : 'https://lti-groups.huit.edu.vn';

            const ensureNavTab = () => {
              if (window.location.pathname.includes('/instructor')) return;
              const cId = getCourseId();
              if (!cId) return;

              const targetUrl = `${ltiBase}/auth/openedxsso/login?course_id=${encodeURIComponent(cId)}`;

              const tabContainers = document.querySelectorAll(
                'nav.nav-tabs, .nav-tabs, nav.course-tabs, .course-tabs-navigation nav, nav[aria-label*="Course" i], nav[aria-label*="Điều hướng" i], nav.navigation-tabs, .learning-header nav'
              );

              tabContainers.forEach(container => {
                if (container.querySelector('#huit-nav-group-assignment')) return;

                const links = container.querySelectorAll('a');
                let isCourseTabs = false;
                links.forEach(a => {
                  const href = a.getAttribute('href') || '';
                  const text = (a.textContent || '').trim().toLowerCase();
                  if (
                    href.includes('/course') || href.includes('/progress') || href.includes('/dates') || href.includes('/discussion') || href.includes('/instructor') || href.includes('/home') ||
                    text.includes('khóa học') || text.includes('tiến triển') || text.includes('ngày') || text.includes('cuộc thảo luận') || text.includes('người hướng dẫn') || text.includes('instructor')
                  ) {
                    isCourseTabs = true;
                  }
                });

                if (!isCourseTabs && !container.classList.contains('nav-tabs')) return;

                const ul = container.tagName.toLowerCase() === 'ul' ? container : container.querySelector('ul');

                const tabLink = document.createElement('a');
                tabLink.id = 'huit-nav-group-assignment';
                tabLink.className = 'nav-item flex-shrink-0 nav-link huit-tab-group-assignment';
                tabLink.href = targetUrl;
                tabLink.target = '_blank';
                tabLink.setAttribute('role', 'tab');
                tabLink.setAttribute('tabindex', '0');
                tabLink.textContent = 'Bài tập nhóm';

                if (ul) {
                  const li = document.createElement('li');
                  li.id = 'huit-nav-group-assignment-li';
                  li.className = 'nav-item';
                  li.appendChild(tabLink);
                  ul.appendChild(li);
                } else {
                  container.appendChild(tabLink);
                }
              });
            };

            ensureNavTab();
            const intervalId = setInterval(ensureNavTab, 300);
            return () => clearInterval(intervalId);
          }, []);

          return null;
        }
      }
    }
  ]);
}

// HUIT MFE Learner Dashboard Branding — Slot wiring.
// DIRECT_PLUGIN and PLUGIN_OPERATIONS are available from the FPF dynamic import above.
// React and HUIT_LEARNER_DASHBOARD_CSS are available from module-level scope.

const sliderImages = [{"alt": "Banner tr\u01b0\u1eddng", "link": "https://huit.edu.vn/", "url": "https://huit.edu.vn/images/slide/banner-web-huit.jpg"}];
const huitDashboardDefaultBanner = "https://files.lms.huit.edu.vn/openedx/openedx/media/course-default-banner.png";


if (process.env.APP_ID === 'learner-dashboard') {
  // 1. Ẩn Sidebar Widget mặc định để giải phóng không gian
  addPlugins(config, 'org.openedx.frontend.learner_dashboard.widget_sidebar.v1', [
    {
      op: PLUGIN_OPERATIONS.Hide,
      widgetId: 'default_contents',
    }
  ]);

  // 2. Ẩn danh sách khóa học mặc định và chèn giao diện Grid 4 Cột tùy chỉnh
  addPlugins(config, 'org.openedx.frontend.learner_dashboard.course_list.v1', [
    {
      op: PLUGIN_OPERATIONS.Hide,
      widgetId: 'default_contents',
    },
    {
      op: PLUGIN_OPERATIONS.Insert,
      widget: {
        id: 'huit_custom_dashboard_v8_final',
        type: DIRECT_PLUGIN,
        priority: 10,
        RenderWidget: ({ courseListData }) => {
          const courses = courseListData?.visibleList || [];
          const LMS_BASE = process.env.LMS_BASE_URL || window.location.origin.replace('apps.', ''); 
          
          // State lưu trữ phần trăm hoàn thành
          const [progressMap, setProgressMap] = React.useState({});

          React.useEffect(() => {
            // Gọi API lấy tiến độ thực tế cho từng khóa học
            courses.forEach(item => {
               const courseId = item.courseRun?.courseId; 
               
               if (courseId) {
                   fetch(`${LMS_BASE}/api/course_home/progress/${courseId}`, { credentials: 'include' })
                     .then(res => {
                         if (!res.ok) throw new Error("Network response was not ok");
                         return res.json();
                     })
                     .then(data => {
                        let percent = 0;
                        if (data && data.completion_summary) {
                           const comp = data.completion_summary.complete_count || 0;
                           const incomp = data.completion_summary.incomplete_count || 0;
                           const total = comp + incomp;
                           if (total > 0) {
                              percent = Math.round((comp / total) * 100);
                           }
                        }
                        setProgressMap(prev => ({...prev, [courseId]: percent}));
                     })
                     .catch(err => console.log(`Lỗi tải API Tiến độ cho ${courseId}:`, err));
               }
            });
          }, [courses, LMS_BASE]);

          return (
            <div style={{ width: '100%' }}>
              <style>{HUIT_LEARNER_DASHBOARD_CSS}</style>
              
              <div className="huit-fullwidth-banner">
                {sliderImages.length > 0 && (
                  <a href={sliderImages[0].link} target="_blank" rel="noopener noreferrer">
                    <img src={sliderImages[0].url} alt={sliderImages[0].alt || 'HUIT Banner'} />
                  </a>
                )}
              </div>

              <div className="huit-grid">
                {courses.map(item => {
                  const course = item.course;
                  const courseRun = item.courseRun;
                  const courseId = courseRun?.courseId;
                  
                  // Lấy tiến độ từ State
                  const progressPercent = courseId && progressMap[courseId] !== undefined ? progressMap[courseId] : 0;
                  
                  // Xử lý ảnh an toàn (tránh lỗi crash)
                  let imgSrc = huitDashboardDefaultBanner; 
                  if (course && course.bannerImgSrc) {
                      imgSrc = course.bannerImgSrc.startsWith('http') 
                                    ? course.bannerImgSrc 
                                    : `${LMS_BASE}${course.bannerImgSrc}`;
                  }
                  
                  // Format ngày tháng
                  const dateStr = courseRun?.startDate ? new Date(courseRun.startDate).toLocaleDateString('vi-VN') : "TBA";

                  return (
                    <div key={courseId || Math.random()} className="huit-card">
                      <img 
                        src={imgSrc} 
                        className="huit-card-img" 
                        alt={course?.courseName || 'Course'} 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = huitDashboardDefaultBanner;
                        }}
                      />
                      
                      <div className="huit-card-content">
                        <h3 className="huit-card-title">{course?.courseName || 'Khóa học chưa có tên'}</h3>

                        <div>
                            <div className="progress-bg">
                               <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
                            </div>
                            <div className="progress-text">{progressPercent}% Hoàn thành</div>
                        </div>
                        
                        <div className="huit-footer-row">
                          <span style={{ fontSize: '0.85rem', color: '#444' }}>
                            <b>Bắt đầu:</b> <br/> {dateStr}
                          </span>
                          <a href={courseRun?.homeUrl || '#'} className="huit-btn-go">Vào học</a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        },
      }
    }
  ]);
}

// HUIT — nút "Trang chủ giảng viên" trên instructor toolbar của Learning MFE.
// Toolbar không có plugin slot nào (đã kiểm tra bundle ulmo), nên chèn thẳng vào
// DOM qua MutationObserver; toolbar chỉ render cho staff nên nút cũng chỉ hiện cho staff.

const HUIT_STUDIO_BASE_FALLBACK = 'https://studio.lms.huit.edu.vn';


if (process.env.APP_ID === 'learning' && typeof document !== 'undefined') {
  const HUIT_STUDIO_HOME_BTN_ID = 'huit-instructor-studio-home';

  const huitStudioHomeUrl = () => {
    const cfg = typeof getConfig === 'function' ? getConfig() : null;
    const base = (cfg && cfg.STUDIO_BASE_URL) || HUIT_STUDIO_BASE_FALLBACK;
    return `${base.replace(/\/+$/, '')}/home`;
  };

  const huitInjectStudioHomeButton = () => {
    document.querySelectorAll('div[data-testid="instructor-toolbar"]').forEach((toolbar) => {
      if (toolbar.querySelector(`#${HUIT_STUDIO_HOME_BTN_ID}`)) return;

      const row = toolbar.querySelector('.container-xl');
      if (!row) return;

      const wrapper = document.createElement('span');
      wrapper.className = 'mx-1 my-1 pr-3';

      const link = document.createElement('a');
      link.id = HUIT_STUDIO_HOME_BTN_ID;
      link.className = 'btn btn-inverse-outline-primary';
      link.href = huitStudioHomeUrl();
      link.textContent = 'Trang chủ giảng viên';
      wrapper.appendChild(link);

      const masqueradeBlock = row.querySelector('.flex-grow-1');
      if (masqueradeBlock && masqueradeBlock.nextSibling) {
        row.insertBefore(wrapper, masqueradeBlock.nextSibling);
      } else {
        row.appendChild(wrapper);
      }
    });
  };

  const huitStartStudioHomeObserver = () => {
    if (!document.body) return;
    huitInjectStudioHomeButton();

    let pending = false;
    const observer = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        huitInjectStudioHomeButton();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', huitStartStudioHomeObserver);
  } else {
    huitStartStudioHomeObserver();
  }
}

    if (process.env.APP_ID == 'admin-console') {
    }
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {
    }
    if (process.env.APP_ID == 'account') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'learning_help_slot', [
        {
            op: PLUGIN_OPERATIONS.Hide,
            widgetId: 'default_contents',
        }
        ]);
      addPlugins(config, 'learning_help_slot', [ 
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'theme_switch_button',
                type: DIRECT_PLUGIN,
                RenderWidget: ToggleThemeButton,
            },
        },
        ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
      addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [ 
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'custom_footer',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: IndigoFooter,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: ToggleThemeButton,
                    },
                },
        ]);
      addPlugins(config, 'mobile_header_slot', [
                {
                    op: PLUGIN_OPERATIONS.Hide,
                    widgetId: 'default_contents',
                }
                ]);
      addPlugins(config, 'mobile_header_slot', [ 
                {
                    op: PLUGIN_OPERATIONS.Insert,
                    widget: {
                        id: 'theme_switch_button',
                        type: DIRECT_PLUGIN,
                        RenderWidget: MobileViewHeader,
                    },
                },
                ]);
    }
    if (process.env.APP_ID == 'learner-record') {
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;